import { z } from 'zod';
import { calendarDateSchema, timeOfDaySchema } from './arrangement.schema';
import { objectIdSchema } from './common.schema';

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  [P7B §2] A TEMPLATE SET — ONE EVENT IN ONE YEAR, IMPORTED FROM ITS BOOKLET.
 *
 *  ⚠️ READ-ONLY FOR EVER. A set and its templates are written by ONE writer — the import's commit,
 *  which creates and never updates — and by no route. They are never published, filled or archived,
 *  and nothing that lists, counts or notifies about arrangements reads them.
 *
 *  ⚠️ A TEMPLATE CARRIES NO DATE. The booklet's dates are last year's; a SET carries its festival DAYS
 *  (ordinal keys, names, and the source year's date for reference), and the calendar for a year maps
 *  each day to a date ([P7B §4b]). Which days a section's duties run on, and at what times, is a
 *  person's decision on that section (`SCHEDULE`, §3) — the extraction carries them only as free text.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */

/** A set's key — `durga-puja-2025`. Lower-case words joined by hyphens; it travels in URLs. */
export const TEMPLATE_SET_KEY_REGEX = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
export const templateSetKeySchema = z.string().regex(TEMPLATE_SET_KEY_REGEX, 'Not a template set');

/** Bounds a list read rather than paging it: a set is read whole, and past this the read THROWS. */
export const TEMPLATE_SET_MAX_TEMPLATES = 500;
/** A booklet's festival days. Thirteen in 2025. */
export const TEMPLATE_DAYS_MAX = 31;
/** Shifts in one section's schedule. */
export const TEMPLATE_SHIFTS_MAX = 4;

/**
 * A festival day. ⚠️ ITS IDENTITY IS `key`, MINTED FROM ITS ORDINAL, NEVER ITS NAME: the 2025 booklet
 * has two days both called "Lakshmi Puja immersion".
 */
export const templateDaySchema = z.object({
  key: z.string().min(1),
  ordinal: z.number().int().min(1),
  name: z.string(),
  note: z.string().nullable(),
  /** The booklet's own date, for reference only — never used to date a duty. */
  sourceDate: calendarDateSchema,
});

export const TEMPLATE_TIMING_KINDS = ['WHOLE_DAY', 'SHIFTS'] as const;
export type TemplateTimingKind = (typeof TEMPLATE_TIMING_KINDS)[number];

/** A shift may run past midnight (end before start); an equal start and end is refused, as publish refuses it. */
export const templateShiftSchema = z
  .object({ start: timeOfDaySchema, end: timeOfDaySchema })
  .refine((shift) => shift.start !== shift.end, { message: 'A shift cannot start and end at the same time' });

export const templateTimingSchema = z.discriminatedUnion('kind', [
  /** The whole IST day, with an optional reporting time — a whole-day card's own shape ([C5b §3e]). */
  z.object({ kind: z.literal('WHOLE_DAY'), reportingTime: timeOfDaySchema.nullable() }),
  z.object({ kind: z.literal('SHIFTS'), shifts: z.array(templateShiftSchema).min(1).max(TEMPLATE_SHIFTS_MAX) }),
]);

/**
 * When a section's duties run: which festival days, and at what times.
 *
 * ⚠️ `nextCalendarDay` MOVES EVERY DATE ON BY ONE — "00.00 to 08.00 next day" is a duty that starts at
 * midnight AFTER the festival day. A shift whose end is before its start ("22.00 to 08.00") is
 * different: it starts ON the festival day and runs past midnight, which publish already handles.
 */
export const templateScheduleSchema = z.object({
  dayKeys: z
    .array(z.string().min(1))
    .min(1, 'Choose at least one festival day')
    .max(TEMPLATE_DAYS_MAX)
    .refine((keys) => new Set(keys).size === keys.length, { message: 'A day is chosen twice' }),
  timing: templateTimingSchema,
  nextCalendarDay: z.boolean(),
});

/** A booklet section — the unit a schedule is decided for. */
export const templateSectionSchema = z.object({
  key: z.string(),
  part: z.string(),
  title: z.string(),
  pages: z.array(z.number().int()),
  /** The booklet's own words about dates and times, shown beside the decision. */
  sourceText: z.array(z.string()),
  /** What the import read from `sourceText` — a SUGGESTION, never applied without a person. */
  proposal: templateScheduleSchema.nullable(),
  /** How the proposal was read, or why there is none. */
  proposalNote: z.string().nullable(),
  /** The booklet calls this section round the clock: flagged, and nothing is built for it. */
  roundTheClock: z.boolean(),
  templateCount: z.number().int().min(0),
});

export const TEMPLATE_DECISION_CLASSES = [
  'SOURCE',
  'FORCE_COLUMN',
  'POLICY',
  'IN_CHARGE_ROLE',
  'TOTALS',
  'SCHEDULE',
  'DUTY_POINT',
] as const;
export type TemplateDecisionClass = (typeof TEMPLATE_DECISION_CLASSES)[number];

export const TEMPLATE_READINESS_STATES = ['READY', 'BLOCKED', 'ROUND_THE_CLOCK', 'EMPTY'] as const;
export type TemplateReadinessState = (typeof TEMPLATE_READINESS_STATES)[number];

/**
 * ⚠️ [P7B §3e] DECISIONS OUTSTANDING, GROUPED — "N decisions affect M templates", one row per class.
 * Never a list of 182 conflicts. [amended P7B.1] Still one row per class: the code-by-code view is
 * each template's own `waitsOn`, which the library gathers into "what holds templates back" — the
 * same rule (`openCodesOf`), held to this count by a parity test.
 */
export const templateDecisionSummarySchema = z.object({
  /** Distinct codes across every class. */
  decisions: z.number().int().min(0),
  outstanding: z.number().int().min(0),
  /** Templates using at least one outstanding code. */
  templatesAffected: z.number().int().min(0),
  classes: z.array(
    z.object({
      class: z.enum(TEMPLATE_DECISION_CLASSES),
      decided: z.number().int().min(0),
      outstanding: z.number().int().min(0),
      templatesAffected: z.number().int().min(0),
    }),
  ),
});

export const templateSetSummarySchema = z.object({
  id: objectIdSchema,
  key: z.string(),
  event: z.string(),
  sourceYear: z.number().int(),
  title: z.string(),
  templateCount: z.number().int().min(0),
  createdAt: z.iso.datetime(),
  /**
   * [DP2026] The booklet's PA number and date, so the sets page can say which booklet a year is — two
   * sets of one event now stand side by side. The full set's `source` below extends this one.
   */
  source: z.object({
    paNumber: z.string().nullable(),
    paDate: calendarDateSchema.nullable(),
  }),
});

export const templateSetSchema = templateSetSummarySchema.extend({
  source: z.object({
    paNumber: z.string().nullable(),
    paDate: calendarDateSchema.nullable(),
    fileName: z.string(),
    sha256: z.string(),
    pageCount: z.number().int().min(0),
  }),
  days: z.array(templateDaySchema),
  sections: z.array(templateSectionSchema),
  stats: z.object({
    dutyPoints: z.number().int().min(0),
    roundTheClock: z.number().int().min(0),
    empty: z.number().int().min(0),
  }),
  decisions: templateDecisionSummarySchema,
  readiness: z.object({
    READY: z.number().int().min(0),
    BLOCKED: z.number().int().min(0),
    ROUND_THE_CLOCK: z.number().int().min(0),
    EMPTY: z.number().int().min(0),
  }),
});

export const templateSetListSchema = z.object({ items: z.array(templateSetSummarySchema) });

export const templateSetParamsSchema = z.object({ setKey: templateSetKeySchema });
